/**
 * MudFullJob — the whole-well mud roll-up (GET /wells/{well_uwi}/full-job/mud).
 * Shows the product inventory across the job, a transactions roll-up (received /
 * used / adjusted / returned + cost), and the server's total product cost.
 *
 * Currency-aware + MIXED-safe: the server returns `currency: "MIXED"` when the
 * well's mud lines span more than one currency, in which case the total is not
 * a single-currency figure — we show the raw total with a MIXED note instead of
 * a misleading currency symbol.
 */
import { useMemo } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { fmtMoney, fmtNum } from "@lib/format";
import type { Locale } from "@i18n/locales";
import { useGetFullJobMudQuery, type MudProductTranWriteOut } from "./mudApi";

/** Sum a numeric field across transaction rows, guarding nulls. */
function sumField(
  rows: MudProductTranWriteOut[],
  pick: (t: MudProductTranWriteOut) => number | null,
): number {
  return rows.reduce((s, t) => {
    const v = pick(t);
    return s + (v != null && Number.isFinite(v) ? v : 0);
  }, 0);
}

/** Format the roll-up total, MIXED-safe. When the currency is "MIXED" (or the
 * lines span currencies) we don't stamp a symbol — the figure isn't a single
 * comparable currency amount. */
function fmtTotalCost(
  total: number | null,
  currency: string | null,
  locale: Locale,
): { text: string; mixed: boolean } {
  const mixed = currency != null && currency.toUpperCase() === "MIXED";
  if (total == null || !Number.isFinite(total)) return { text: "—", mixed };
  if (mixed) return { text: fmtNum(total, locale, { maximumFractionDigits: 0 }), mixed: true };
  return { text: fmtMoney(total, locale, { currency: currency ?? undefined }), mixed: false };
}

export function MudFullJob({ wellUwi }: { wellUwi: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetFullJobMudQuery(wellUwi, {
    skip: !wellUwi,
  });

  const products = Array.isArray(data?.products) ? data!.products : [];
  const transactions = Array.isArray(data?.transactions) ? data!.transactions : [];

  const total = useMemo(
    () => fmtTotalCost(data?.total_product_cost ?? null, data?.currency ?? null, locale),
    [data?.total_product_cost, data?.currency, locale],
  );

  const stats: Stat[] = data
    ? [
        { label: t("mud.kpi.products"), value: fmtNum(products.length, locale) },
        {
          label: t("mud.kpi.received"),
          value: fmtNum(sumField(transactions, (x) => x.quantity_received), locale, {
            maximumFractionDigits: 1,
          }),
        },
        {
          label: t("mud.kpi.used"),
          value: fmtNum(sumField(transactions, (x) => x.quantity_used), locale, {
            maximumFractionDigits: 1,
          }),
          tone: "ops",
        },
        {
          label: total.mixed ? t("mud.fullJob.totalMixed") : t("mud.kpi.cost"),
          value: total.text,
        },
      ]
    : [];

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && products.length === 0 && transactions.length === 0}
      emptyMessage={t("mud.fullJob.empty")}
      onRetry={refetch}
    >
      <div className="page-head" style={{ marginBottom: 12 }}>
        <p className="subtitle">{t("mud.fullJob.subtitle")}</p>
      </div>
      {stats.length ? <StatBand stats={stats} /> : null}

      {/* Product inventory across the whole job */}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("mud.products")}</h3>
        {products.length === 0 ? (
          <div className="empty-state">{t("mud.products.empty")}</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("mud.col.product")}</th>
                  <th>{t("mud.col.vendor")}</th>
                  <th className="num">{t("mud.col.unitPrice")}</th>
                  <th className="num">{t("mud.col.unitSize")}</th>
                  <th>{t("mud.col.unitMeasure")}</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} data-owner="p">
                    <td>{p.product_name ?? "—"}</td>
                    <td>{p.vendor_entry_key ?? "—"}</td>
                    <td className="num tab-nums">
                      {p.unit_price != null
                        ? fmtMoney(p.unit_price, locale, { currency: p.currency ?? undefined })
                        : "—"}
                    </td>
                    <td className="num tab-nums">
                      {fmtNum(p.unit_size, locale, { maximumFractionDigits: 2 })}
                    </td>
                    <td>{p.unit_measure_entry_key ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Transactions roll-up (whole-job totals + total product cost) */}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("mud.fullJob.rollup")}</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th className="num">{t("mud.col.received")}</th>
                <th className="num">{t("mud.col.used")}</th>
                <th className="num">{t("mud.col.adjusted")}</th>
                <th className="num">{t("mud.col.returned")}</th>
                <th className="num">{t("mud.kpi.cost")}</th>
              </tr>
            </thead>
            <tbody>
              <tr data-owner="p">
                <td className="num tab-nums">
                  {fmtNum(sumField(transactions, (x) => x.quantity_received), locale, {
                    maximumFractionDigits: 1,
                  })}
                </td>
                <td className="num tab-nums">
                  {fmtNum(sumField(transactions, (x) => x.quantity_used), locale, {
                    maximumFractionDigits: 1,
                  })}
                </td>
                <td className="num tab-nums">
                  {fmtNum(sumField(transactions, (x) => x.quantity_adjusted), locale, {
                    maximumFractionDigits: 1,
                  })}
                </td>
                <td className="num tab-nums">
                  {fmtNum(sumField(transactions, (x) => x.quantity_returned), locale, {
                    maximumFractionDigits: 1,
                  })}
                </td>
                <td className="num tab-nums">
                  {total.text}
                  {total.mixed ? (
                    <span className="badge" style={{ marginLeft: 6 }}>
                      {t("mud.fullJob.mixed")}
                    </span>
                  ) : null}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="subtitle" style={{ marginTop: 8 }}>
          {t("mud.fullJob.transactionCount")}: {fmtNum(transactions.length, locale)}
        </p>
      </div>
    </QueryState>
  );
}
